import React, { useState } from 'react'
import { setSnackInfo } from '../redux/actions/appAction'
import { connect } from 'react-redux'
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Grid, Button, TextField, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Avatar, Chip, Tooltip, IconButton, InputAdornment
} from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';
import Skeleton from '@material-ui/lab/Skeleton';
import moment from "moment";
import HttpService from '../services/httpService';

import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import PersonIcon from '@material-ui/icons/Person';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import AssignmentIcon from '@material-ui/icons/Assignment';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import SearchOffIcon from '@material-ui/icons/FindInPage';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

// Shared Decorpot palette - the same constants as users, roles, Xoxoday and
// cronJobMonitor, so this page recolours with them instead of carrying its own.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const INK = "#3d2b21";
const GREEN_DARK = "#19692c";
const GREEN_LIGHT = "#eaf6ee";

// An icon per search type, so the dropdown reads as a list of things rather
// than a list of words.
const SEARCH_ICONS = {
    'Customer Name': PersonIcon,
    'Email ID': MailOutlineIcon,
    'Lead No': AssignmentIcon,
    'Project No': BusinessCenterIcon,
    'Contact No': PhoneIcon,
    'Unique Lead No': FingerprintIcon,
};

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: ORANGE_DARK,
        color: "#fff",
        fontWeight: 700,
        fontSize: 12.5,
        letterSpacing: 0.3,
        borderBottom: "none",
        whiteSpace: "nowrap",
        padding: "10px 12px",
        // stickyHeader sets position:sticky/top:0 on head cells - zIndex keeps
        // this background painted above the scrolling rows at the seam.
        zIndex: 3,
    },
    body: {
        fontSize: 13,
        color: INK,
        padding: "8px 12px",
    },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        transition: "background-color 150ms ease",
        '&:nth-of-type(even)': { backgroundColor: "#fafbfd" },
        '&:hover': { backgroundColor: ORANGE_LIGHT },
    },
}))(TableRow);

const useStyles = makeStyles((theme) => ({
    root: { paddingBottom: 32 },

    banner: {
        display: "flex", alignItems: "center", gap: 16,
        padding: "14px 22px", borderRadius: 16,
        background: HEADER_GRADIENT, color: "#fff",
        boxShadow: "0 8px 24px rgba(93,55,28,0.18)", marginBottom: 14,
        [theme.breakpoints.down('xs')]: { padding: "14px 16px", flexWrap: "wrap" },
    },
    bannerIcon: {
        display: "grid", placeItems: "center", width: 44, height: 44,
        borderRadius: 14, background: "rgba(255,255,255,0.18)", flexShrink: 0,
    },
    bannerTitle: { fontSize: 19, fontWeight: 700, lineHeight: 1.3 },
    bannerSub: { fontSize: 12.5, color: "rgba(255,255,255,0.88)", marginTop: 3 },

    card: {
        borderRadius: 16, background: "#fff",
        border: "1px solid #eceff3",
        boxShadow: "0 4px 24px rgba(93,55,28,0.08)",
        padding: "18px 20px",
        [theme.breakpoints.down('xs')]: { padding: "16px" },
    },
    sectionLabel: {
        display: "flex", alignItems: "center", gap: 8, marginBottom: 14,
        fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
        textTransform: "uppercase", color: "#756e68",
    },
    sectionRule: { flex: 1, height: 1, background: "#f1eeec" },

    searchBtn: {
        borderRadius: 24, textTransform: "none", fontWeight: 700,
        padding: "8px 26px", height: 40,
        background: ORANGE_DARK, color: "#fff", boxShadow: "0 6px 16px rgba(93,55,28,0.22)",
        "&:hover": { background: "#a8450f", boxShadow: "0 6px 16px rgba(93,55,28,0.22)" },
        "&.Mui-disabled": { background: "#e6e2df", color: "#857d77", boxShadow: "none" },
    },
    ghostBtn: {
        borderRadius: 24, textTransform: "none", fontWeight: 600,
        padding: "8px 18px", height: 40,
        color: "#6b6460", background: "#f3f1ef", boxShadow: "none",
        "&:hover": { background: "#eae6e3", boxShadow: "none" },
    },
    hintBar: {
        display: "flex", alignItems: "flex-start", gap: 9,
        padding: "8px 12px", marginTop: 14, borderRadius: 12,
        background: "#eef3fc", border: "1px solid #cfdcf3",
        fontSize: 12, color: "#24487f", lineHeight: 1.55,
    },

    // Reserved rather than conditional, so starting a search does not jog the
    // card down by 4px and back again.
    progressSlot: { height: 4, marginBottom: 10 },
    loadingNote: {
        display: "flex", alignItems: "center", gap: 10,
        padding: "7px 13px", marginBottom: 12, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingNoteTitle: { fontSize: 12.5, fontWeight: 700, color: ORANGE_DARK },
    loadingNoteHint: { fontSize: 11.5, color: "#7a6a60", marginTop: 1 },
    // The row being saved keeps its place and its data; only its own controls
    // go busy. Nothing else on the page is blocked.
    savingRow: { opacity: 0.75 },

    resultsHead: {
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        marginBottom: 12,
    },
    countChip: {
        height: 24, fontSize: 11.5, fontWeight: 800,
        background: ORANGE_LIGHT, color: "#a8450f",
    },
    // Capped rather than locked to the viewport: this page is a short form
    // above a variable list, so the document scrolls normally and only a long
    // result set gets its own scroller.
    tableWrap: {
        borderRadius: 12, border: "1px solid #eceff3",
        maxHeight: "58vh", overflow: "auto",
    },
    slCell: { width: 54, fontSize: 12.5, color: "#636c7a", fontWeight: 700 },
    nameCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    nameAvatar: {
        width: 30, height: 30, fontSize: 12.5, fontWeight: 700,
        background: ORANGE_LIGHT, color: ORANGE_DARK, flexShrink: 0,
    },
    nameMain: { fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1.3 },
    mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 },
    stageChip: {
        height: 22, fontSize: 11.5, fontWeight: 700,
        background: "#eef1f5", color: "#4f596a",
    },
    wonChip: {
        height: 24, fontSize: 12, fontWeight: 700,
        background: GREEN_LIGHT, color: GREEN_DARK, border: "1px solid #bfe3c9",
    },
    muted: { color: "#636c7a", fontStyle: "italic", fontSize: 12.5 },
    // The row being edited lifts out of the stripe pattern, so there is never
    // a doubt about which date field belongs to which customer.
    editingRow: {
        background: "#fff8f4 !important",
        boxShadow: `inset 3px 0 0 0 ${ORANGE_DARK}`,
    },
    dateInput: { width: 168 },
    saveBtn: {
        borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: 12,
        padding: "4px 14px", background: GREEN_DARK, color: "#fff", boxShadow: "none",
        "&:hover": { background: "#11492a", boxShadow: "none" },
    },
    cancelBtn: {
        borderRadius: 20, textTransform: "none", fontWeight: 600, fontSize: 12,
        padding: "4px 12px", color: "#6b6460",
    },
    editBtn: {
        borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: 12,
        padding: "4px 14px",
        color: ORANGE_DARK, border: `1px solid rgba(244,126,68,0.55)`,
        "&.Mui-disabled": { color: "#a8a09a", border: "1px solid #e6e2df" },
    },

    emptyWrap: { textAlign: "center", padding: "52px 20px" },
    emptyTitle: { fontSize: 15, fontWeight: 700, color: "#5f6876", marginTop: 10 },
    emptyHint: {
        fontSize: 12.5, color: "#636c7a", marginTop: 4,
        maxWidth: 420, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6,
    },
}))

const UpdateWonDate = (props) => {

    const classes = useStyles()

    const [searchInput, setSearchInput] = useState("")
    const [searchTypes] = useState([
        { name: 'Customer Name' },
        { name: 'Email ID' },
        { name: 'Lead No' },
        { name: 'Project No' },
        { name: 'Contact No' },
        { name: 'Unique Lead No' }
    ])
    const [selectedSearchType, setSelectedSearchType] = useState("")
    const [leadResults, setLeadResults] = useState([])
    const [editLeadId, setEditLeadId] = useState(null)
    const [newWonDate, setNewWonDate] = useState("")
    // Display only: tells the empty state apart from "you have not searched
    // yet", which used to read "No leads found." before anything was asked.
    const [hasSearched, setHasSearched] = useState(false)
    // Two loaders instead of one global blocker.
    //
    // props.setLoading drops a backdrop over the whole app, so searching froze
    // the sidebar and saving one row froze every other row - including the
    // Cancel button of the row you were editing. These are scoped to the thing
    // actually being waited on.
    const [searching, setSearching] = useState(false)
    const [savingId, setSavingId] = useState(null)

    const handleSearch = async () => {

        if (!selectedSearchType || !searchInput) {
            props.setSnackInfo("Please select search type and enter value", 'warning');
            return
        }

        try {

            setSearching(true)

            const res = await HttpService.getAllSearchLeads(
                selectedSearchType,
                searchInput,
                null,
                {}
            )

            if (res.data && res.data.docs) {

                let allLeads = []

                res.data.docs.forEach(stage => {
                    if (stage.projects && stage.projects.length > 0) {
                        allLeads = [...allLeads, ...stage.projects]
                    }
                })

                setLeadResults(allLeads)

            } else {
                setLeadResults([])
            }

        } catch (error) {
            console.error(error)
        } finally {
            setHasSearched(true)
            setSearching(false)
        }
    }


    const handleEditClick = (lead) => {
        setEditLeadId(lead._id)
        setNewWonDate(lead.leadWonDate ? lead.leadWonDate.split("T")[0] : "")
    }

    const updateWonDate = async (leadId) => {

        try {

            setSavingId(leadId)

            const obj = {
                leadId,
                newWonDate
            }
            const res = await HttpService.updateSalesWonDate(obj)
            if (res?.data?.success) {
                props.setSnackInfo(res.data.message || "Update successful...", "success")
                const updated = leadResults.map(l => {
                    if (l._id === leadId) {
                        return { ...l, leadWonDate: newWonDate }
                    }
                    return l
                })

                setLeadResults(updated)
                setEditLeadId(null)
            }
        } catch (error) {
            console.error(error)
            props.setSnackInfo("Updation failed/Lead Not Eligible for update", "error")
        } finally {
            setSavingId(null)
        }
    }

    const today = new Date().toISOString().split("T")[0]
    const canSearch = Boolean(selectedSearchType && searchInput)

    return (
        <div className={classes.root}>

            <div className={classes.banner}>
                <div className={classes.bannerIcon}><EventAvailableIcon style={{ fontSize: 24 }} /></div>
                <div style={{ minWidth: 0 }}>
                    <div className={classes.bannerTitle}>Update Sales Won Date</div>
                    <div className={classes.bannerSub}>
                        Find a lead, then correct the date its sale was won.
                    </div>
                </div>
            </div>

            <div className={classes.card}>

                <div className={classes.sectionLabel}>
                    <SearchIcon style={{ fontSize: 15 }} /> Find a lead
                    <span className={classes.sectionRule} />
                </div>

                <Grid container spacing={2} alignItems="flex-start">

                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Search by"
                            value={selectedSearchType}
                            onChange={(e) => setSelectedSearchType(e.target.value)}
                        >
                            {searchTypes.map((type, i) => {
                                const Icon = SEARCH_ICONS[type.name] || AssignmentIcon
                                return (
                                    <MenuItem key={i} value={type.name}>
                                        <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                            <Icon style={{ fontSize: 16, color: ORANGE_DARK }} />
                                            {type.name}
                                        </span>
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={5} md={5}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label={selectedSearchType ? `Enter ${selectedSearchType}` : "Search value"}
                            placeholder={selectedSearchType ? '' : 'Pick a search type first'}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && canSearch) handleSearch() }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: "#9aa5b1" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="Clear">
                                            <IconButton size="small" onClick={() => setSearchInput("")} aria-label="clear">
                                                <ClearIcon style={{ fontSize: 15 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3} md={4}>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <Button
                                className={classes.searchBtn}
                                onClick={handleSearch}
                                disabled={searching}
                                startIcon={searching
                                    ? <CircularProgress size={15} thickness={5} style={{ color: "#fff" }} />
                                    : <SearchIcon style={{ fontSize: 17 }} />}
                            >
                                {searching ? "Searching…" : "Search"}
                            </Button>
                            {(searchInput || selectedSearchType) && (
                                <Button
                                    className={classes.ghostBtn}
                                    disabled={searching}
                                    onClick={() => { setSearchInput(""); setSelectedSearchType(""); }}
                                    startIcon={<ClearIcon style={{ fontSize: 16 }} />}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </Grid>

                </Grid>

                <div className={classes.hintBar}>
                    <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                    <span>
                        Only leads that already carry a won date can be edited, and the new date
                        cannot be in the future.
                    </span>
                </div>

            </div>

            <div className={classes.progressSlot}>
                {searching ? <BrandLinearProgress /> : null}
            </div>

            {searching && (
                <div className={classes.loadingNote}>
                    <CircularProgress size={15} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div>
                        <div className={classes.loadingNoteTitle}>Searching leads…</div>
                        <div className={classes.loadingNoteHint}>
                            Matching “{searchInput}” on {selectedSearchType || "the selected field"} across every stage.
                        </div>
                    </div>
                </div>
            )}

            {searching && leadResults.length === 0 ? (

                <div className={classes.card}>
                    <div className={classes.sectionLabel}>
                        <AssignmentIcon style={{ fontSize: 15 }} /> Matching leads
                        <span className={classes.sectionRule} />
                    </div>
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} height={40} style={{ marginBottom: 2 }} />
                    ))}
                </div>

            ) : leadResults.length > 0 ? (

                <div className={classes.card} style={{ marginTop: 16 }}>

                    <div className={classes.resultsHead}>
                        <div className={classes.sectionLabel} style={{ margin: 0 }}>
                            <AssignmentIcon style={{ fontSize: 15 }} /> Matching leads
                        </div>
                        <Chip size="small" className={classes.countChip} label={leadResults.length} />
                        <span style={{ marginLeft: "auto", fontSize: 12, color: "#5f6876", display: "flex", alignItems: "center", gap: 5 }}>
                            <TouchAppIcon style={{ fontSize: 14 }} />
                            Use <b style={{ margin: "0 3px" }}>Edit</b> to change a won date
                        </span>
                    </div>

                    <TableContainer className={classes.tableWrap}>

                        <Table size="small" stickyHeader>

                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Sl No</StyledTableCell>
                                    <StyledTableCell>Customer Name</StyledTableCell>
                                    <StyledTableCell>Lead No</StyledTableCell>
                                    <StyledTableCell>Mobile</StyledTableCell>
                                    <StyledTableCell>Current Stage</StyledTableCell>
                                    <StyledTableCell>Sales Won Date</StyledTableCell>
                                    <StyledTableCell align="center">Action</StyledTableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {leadResults.map((lead, i) => {

                                    const isEditing = editLeadId === lead._id
                                    const isSaving = savingId === lead._id

                                    return (
                                        <StyledTableRow
                                            key={lead._id}
                                            className={[
                                                isEditing ? classes.editingRow : '',
                                                isSaving ? classes.savingRow : '',
                                            ].filter(Boolean).join(' ') || undefined}
                                        >

                                            <StyledTableCell className={classes.slCell}>{i + 1}</StyledTableCell>

                                            <StyledTableCell>
                                                <span className={classes.nameCell}>
                                                    <Avatar className={classes.nameAvatar}>
                                                        {String(lead.customerId?.name || "?").trim().charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <span className={classes.nameMain}>
                                                        {lead.customerId?.name || <span className={classes.muted}>Unnamed</span>}
                                                    </span>
                                                </span>
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                <span className={classes.mono}>{lead.lead_no}</span>
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                <span className={classes.mono}>
                                                    {lead.customerId?.contact_no || <span className={classes.muted}>—</span>}
                                                </span>
                                            </StyledTableCell>

                                            <StyledTableCell>
                                                {lead.currentStage
                                                    ? <Chip size="small" className={classes.stageChip} label={lead.currentStage} />
                                                    : <span className={classes.muted}>—</span>}
                                            </StyledTableCell>

                                            <StyledTableCell>

                                                {isEditing ? (

                                                    <TextField
                                                        type="date"
                                                        size="small"
                                                        variant="outlined"
                                                        autoFocus
                                                        className={classes.dateInput}
                                                        value={newWonDate}
                                                        disabled={isSaving}
                                                        onChange={(e) => setNewWonDate(e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{ max: today }}
                                                        helperText={isSaving ? "Saving…" : "Cannot be in the future"}
                                                    />

                                                ) : (

                                                    lead?.leadWonDate
                                                        ? <Chip
                                                            size="small"
                                                            className={classes.wonChip}
                                                            icon={<EventAvailableIcon style={{ fontSize: 14, color: GREEN_DARK }} />}
                                                            label={moment(lead.leadWonDate).format("DD-MM-YYYY")}
                                                        />
                                                        : <span className={classes.muted}>NA</span>

                                                )}

                                            </StyledTableCell>

                                            <StyledTableCell align="center">

                                                {isEditing ? (

                                                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                                                        <Button
                                                            className={classes.saveBtn}
                                                            disabled={isSaving}
                                                            startIcon={isSaving
                                                                ? <CircularProgress size={14} thickness={5} style={{ color: "#fff" }} />
                                                                : <CheckIcon style={{ fontSize: 15 }} />}
                                                            onClick={() => updateWonDate(lead._id)}
                                                        >
                                                            {isSaving ? "Saving…" : "Update"}
                                                        </Button>

                                                        <Button
                                                            className={classes.cancelBtn}
                                                            // Cancel used to be swallowed by the global
                                                            // backdrop along with everything else; now it
                                                            // is simply unavailable for the one row that
                                                            // is mid-save.
                                                            disabled={isSaving}
                                                            onClick={() => setEditLeadId(null)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>

                                                ) : (

                                                    // The disabled state used to give no reason at all; the
                                                    // span is there because a Tooltip needs a live child.
                                                    <Tooltip title={lead.leadWonDate
                                                        ? "Change this won date"
                                                        : "This lead has no won date yet, so there is nothing to correct"}>
                                                        <span>
                                                            <Button
                                                                variant="outlined"
                                                                className={classes.editBtn}
                                                                startIcon={<EditIcon style={{ fontSize: 15 }} />}
                                                                disabled={!lead.leadWonDate || Boolean(savingId)}
                                                                onClick={() => handleEditClick(lead)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </span>
                                                    </Tooltip>

                                                )}

                                            </StyledTableCell>

                                        </StyledTableRow>
                                    )
                                })}

                            </TableBody>

                        </Table>

                    </TableContainer>

                </div>

            ) : (

                <div className={classes.card} style={{ marginTop: 16 }}>
                    <div className={classes.emptyWrap}>
                        {hasSearched ? (
                            <>
                                <SearchOffIcon style={{ fontSize: 50, color: "#c9d2de" }} />
                                <div className={classes.emptyTitle}>No leads found</div>
                                <div className={classes.emptyHint}>
                                    Nothing matched that {selectedSearchType || "search"}. Check the value,
                                    or try a different search type.
                                </div>
                            </>
                        ) : (
                            <>
                                <SearchIcon style={{ fontSize: 50, color: "#c9d2de" }} />
                                <div className={classes.emptyTitle}>Search for a lead to begin</div>
                                <div className={classes.emptyHint}>
                                    Pick what you want to search by — name, email, lead number, project
                                    number, contact number or unique lead number — then enter the value above.
                                </div>
                            </>
                        )}
                    </div>
                </div>

            )}

        </div>
    )
}

export default connect(null, { setSnackInfo })(UpdateWonDate)
